import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GripVertical,
  Eye,
  EyeOff,
  Settings,
  Save,
  Upload,
  RotateCcw,
  Plus,
  Trash2,
  Monitor,
  Tablet,
  Smartphone,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  History,
  AlertTriangle,
  Check,
  Layout,
  Image,
  Star,
  TrendingUp,
  Clock,
  Sparkles,
  Palette,
  Megaphone,
  Mail,
  Grid
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../App';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { API_URL } from '../../config/api';

const API = API_URL;

// Block type icons
const BLOCK_ICONS = {
  HeroCarouselBlock: Image,
  FeaturedSeriesBlock: Star,
  TrendingBlock: TrendingUp,
  NewReleasesBlock: Clock,
  GenreSpotlightBlock: Grid,
  FeaturedCreatorsBlock: Sparkles,
  FeaturedArtworksBlock: Palette,
  CTABannerBlock: Megaphone,
  NewsletterSignupBlock: Mail,
};

// Block preview colors
const BLOCK_COLORS = {
  HeroCarouselBlock: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
  FeaturedSeriesBlock: 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30',
  TrendingBlock: 'from-red-500/20 to-pink-500/20 border-red-500/30',
  NewReleasesBlock: 'from-green-500/20 to-emerald-500/20 border-green-500/30',
  GenreSpotlightBlock: 'from-purple-500/20 to-violet-500/20 border-purple-500/30',
  FeaturedCreatorsBlock: 'from-pink-500/20 to-rose-500/20 border-pink-500/30',
  FeaturedArtworksBlock: 'from-indigo-500/20 to-blue-500/20 border-indigo-500/30',
  CTABannerBlock: 'from-orange-500/20 to-red-500/20 border-orange-500/30',
  NewsletterSignupBlock: 'from-teal-500/20 to-cyan-500/20 border-teal-500/30',
};

// Draggable Block Item
const DraggableBlock = ({ 
  block, 
  index, 
  isSelected, 
  onSelect, 
  onToggle, 
  onMoveUp, 
  onMoveDown, 
  onDelete,
  canMoveUp,
  canMoveDown,
  approvedBlocks 
}) => {
  const Icon = BLOCK_ICONS[block.type] || Layout;
  const colorClass = BLOCK_COLORS[block.type] || 'from-zinc-500/20 to-zinc-600/20 border-zinc-500/30';
  const blockInfo = approvedBlocks?.[block.type] || {};
  
  return (
    <div
      className={`
        relative p-3 rounded-lg border cursor-pointer transition-all
        bg-gradient-to-r ${colorClass}
        ${isSelected ? 'ring-2 ring-orange-500' : ''}
        ${!block.enabled ? 'opacity-50' : ''}
      `}
      onClick={() => onSelect(block.id)}
      data-testid={`block-item-${block.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <div className="flex flex-col gap-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={!canMoveUp}
            className={`p-1 rounded hover:bg-white/10 ${!canMoveUp ? 'opacity-30' : ''}`}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={!canMoveDown}
            className={`p-1 rounded hover:bg-white/10 ${!canMoveDown ? 'opacity-30' : ''}`}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
        
        {/* Block Icon */}
        <div className="p-2 rounded-lg bg-white/10">
          <Icon className="w-5 h-5" />
        </div>
        
        {/* Block Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-white text-sm truncate">
            {blockInfo.name || block.type}
          </p>
          <p className="text-xs text-zinc-400 truncate">
            {blockInfo.description || 'Content block'}
          </p>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-2">
          <Switch
            checked={block.enabled}
            onCheckedChange={(checked) => { 
              onToggle(checked);
            }}
            onClick={(e) => e.stopPropagation()}
            className="data-[state=checked]:bg-green-500"
          />
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded hover:bg-red-500/20 text-zinc-400 hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Block Settings Panel
const BlockSettingsPanel = ({ 
  block, 
  approvedBlocks, 
  settingLimits,
  onUpdateSettings 
}) => {
  if (!block) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500">
        <p>Select a block to edit settings</p>
      </div>
    );
  }
  
  const blockInfo = approvedBlocks?.[block.type] || {};
  const defaults = blockInfo.default_settings || {};
  const settings = { ...defaults, ...block.settings };
  
  const handleChange = (key, value) => {
    onUpdateSettings(block.id, { ...block.settings, [key]: value });
  };
  
  const handleColumnChange = (device, value) => {
    const newColumns = { ...(settings.columns || {}), [device]: value };
    handleChange('columns', newColumns);
  };
  
  return (
    <div className="space-y-4" data-testid="block-settings-panel">
      <div className="flex items-center gap-2 pb-2 border-b border-zinc-700">
        {(() => {
          const Icon = BLOCK_ICONS[block.type] || Layout;
          return <Icon className="w-5 h-5 text-orange-400" />;
        })()}
        <h3 className="font-semibold text-white">{blockInfo.name || block.type}</h3>
      </div>
      
      {/* Common Settings */}
      {'title' in defaults && (
        <div className="space-y-2">
          <Label className="text-zinc-400">Section Title</Label>
          <Input
            value={settings.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            className="bg-zinc-800 border-zinc-700"
            placeholder="Section title"
          />
        </div>
      )}
      
      {'subtitle' in defaults && (
        <div className="space-y-2">
          <Label className="text-zinc-400">Subtitle</Label>
          <Input
            value={settings.subtitle || ''}
            onChange={(e) => handleChange('subtitle', e.target.value)}
            className="bg-zinc-800 border-zinc-700"
            placeholder="Subtitle text"
          />
        </div>
      )}
      
      {/* Layout Settings */}
      {'layout' in defaults && (
        <div className="space-y-2">
          <Label className="text-zinc-400">Layout</Label>
          <Select 
            value={settings.layout || 'carousel'} 
            onValueChange={(value) => handleChange('layout', value)}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="carousel">Carousel (Scrollable)</SelectItem>
              <SelectItem value="grid">Grid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      {'density' in defaults && (
        <div className="space-y-2">
          <Label className="text-zinc-400">Density</Label>
          <Select 
            value={settings.density || 'comfy'} 
            onValueChange={(value) => handleChange('density', value)}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="compact">Compact</SelectItem>
              <SelectItem value="comfy">Comfortable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Column Settings */}
      {'columns' in defaults && (
        <div className="space-y-3">
          <Label className="text-zinc-400">Columns</Label>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Monitor className="w-3 h-3" />
                Desktop
              </div>
              <Input
                type="number"
                min={settingLimits?.['columns.desktop']?.min || 2}
                max={settingLimits?.['columns.desktop']?.max || 8}
                value={settings.columns?.desktop || 5}
                onChange={(e) => handleColumnChange('desktop', parseInt(e.target.value))}
                className="bg-zinc-800 border-zinc-700 h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Tablet className="w-3 h-3" />
                Tablet
              </div>
              <Input
                type="number"
                min={settingLimits?.['columns.tablet']?.min || 2}
                max={settingLimits?.['columns.tablet']?.max || 6}
                value={settings.columns?.tablet || 3}
                onChange={(e) => handleColumnChange('tablet', parseInt(e.target.value))}
                className="bg-zinc-800 border-zinc-700 h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Smartphone className="w-3 h-3" />
                Mobile
              </div>
              <Input
                type="number"
                min={settingLimits?.['columns.mobile']?.min || 1}
                max={settingLimits?.['columns.mobile']?.max || 4}
                value={settings.columns?.mobile || 2}
                onChange={(e) => handleColumnChange('mobile', parseInt(e.target.value))}
                className="bg-zinc-800 border-zinc-700 h-8 text-sm"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Card Scale */}
      {'card_scale' in defaults && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-zinc-400">Card Scale</Label>
            <span className="text-sm text-zinc-500">{Math.round((settings.card_scale || 1) * 100)}%</span>
          </div>
          <Slider
            value={[(settings.card_scale || 1) * 100]}
            onValueChange={([value]) => handleChange('card_scale', value / 100)}
            min={60}
            max={100}
            step={5}
            className="py-2"
          />
        </div>
      )}
      
      {/* Max Image Height */}
      {'max_image_height' in defaults && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-zinc-400">Max Image Height</Label>
            <span className="text-sm text-zinc-500">{settings.max_image_height || 280}px</span>
          </div>
          <Slider
            value={[settings.max_image_height || 280]}
            onValueChange={([value]) => handleChange('max_image_height', value)}
            min={settingLimits?.max_image_height?.min || 100}
            max={settingLimits?.max_image_height?.max || 500}
            step={20}
            className="py-2"
          />
        </div>
      )}
      
      {/* Max Items */}
      {'max_items' in defaults && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-zinc-400">Max Items</Label>
            <span className="text-sm text-zinc-500">{settings.max_items || 12}</span>
          </div>
          <Slider
            value={[settings.max_items || 12]}
            onValueChange={([value]) => handleChange('max_items', value)}
            min={settingLimits?.max_items?.min || 4}
            max={settingLimits?.max_items?.max || 24}
            step={1}
            className="py-2"
          />
        </div>
      )}
      
      {/* Hero-specific settings */}
      {'auto_play' in defaults && (
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400">Auto-play</Label>
          <Switch
            checked={settings.auto_play !== false}
            onCheckedChange={(checked) => handleChange('auto_play', checked)}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
      )}
      
      {'show_navigation' in defaults && (
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400">Show Navigation</Label>
          <Switch
            checked={settings.show_navigation !== false}
            onCheckedChange={(checked) => handleChange('show_navigation', checked)}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
      )}
      
      {'show_rank' in defaults && (
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400">Show Rank Numbers</Label>
          <Switch
            checked={settings.show_rank !== false}
            onCheckedChange={(checked) => handleChange('show_rank', checked)}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
      )}
      
      {/* CTA-specific settings */}
      {'button_text' in defaults && (
        <div className="space-y-2">
          <Label className="text-zinc-400">Button Text</Label>
          <Input
            value={settings.button_text || ''}
            onChange={(e) => handleChange('button_text', e.target.value)}
            className="bg-zinc-800 border-zinc-700"
            placeholder="Button text"
          />
        </div>
      )}
      
      {'button_link' in defaults && (
        <div className="space-y-2">
          <Label className="text-zinc-400">Button Link</Label>
          <Input
            value={settings.button_link || ''}
            onChange={(e) => handleChange('button_link', e.target.value)}
            className="bg-zinc-800 border-zinc-700"
            placeholder="/path"
          />
        </div>
      )}
      
      {'show_to_logged_in' in defaults && (
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400">Show to Logged In Users</Label>
          <Switch
            checked={settings.show_to_logged_in === true}
            onCheckedChange={(checked) => handleChange('show_to_logged_in', checked)}
            className="data-[state=checked]:bg-green-500"
          />
        </div>
      )}
    </div>
  );
};

// Main Page Builder Component
const PageBuilder = () => {
  useDocumentTitle('Page Builder - Admin');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  const [draft, setDraft] = useState(null);
  const [published, setPublished] = useState(null);
  const [history, setHistory] = useState([]);
  const [approvedBlocks, setApprovedBlocks] = useState({});
  const [settingLimits, setSettingLimits] = useState({});
  
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, tablet, mobile
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAddBlockDialog, setShowAddBlockDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [rollbackVersion, setRollbackVersion] = useState(null);

  // Check admin access
  useEffect(() => {
    if (user && !user.is_admin && user.role !== 'admin') {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  // Fetch layout data
  const fetchLayout = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/page-layouts/home`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDraft(response.data.draft);
      setPublished(response.data.published);
      setHistory(response.data.history || []);
      setApprovedBlocks(response.data.approved_blocks || {});
      setSettingLimits(response.data.setting_limits || {});
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error fetching layout:', error);
      toast.error('Failed to load page layout');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  // Get current blocks from draft
  const blocks = draft?.blocks || [];
  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  // Update block in draft
  const updateBlock = (blockId, updates) => {
    setDraft(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => 
        b.id === blockId ? { ...b, ...updates } : b
      )
    }));
    setHasUnsavedChanges(true);
  };

  // Toggle block enabled
  const toggleBlock = (blockId, enabled) => {
    updateBlock(blockId, { enabled });
  };

  // Update block settings
  const updateBlockSettings = (blockId, settings) => {
    updateBlock(blockId, { settings });
  };

  // Move block up
  const moveBlockUp = (blockId) => {
    const index = blocks.findIndex(b => b.id === blockId);
    if (index > 0) {
      const newBlocks = [...blocks];
      [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
      setDraft(prev => ({ ...prev, blocks: newBlocks }));
      setHasUnsavedChanges(true);
    }
  };

  // Move block down
  const moveBlockDown = (blockId) => {
    const index = blocks.findIndex(b => b.id === blockId);
    if (index < blocks.length - 1) {
      const newBlocks = [...blocks];
      [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
      setDraft(prev => ({ ...prev, blocks: newBlocks }));
      setHasUnsavedChanges(true);
    }
  };

  // Delete block
  const deleteBlock = (blockId) => {
    if (!window.confirm('Remove this block from the layout?')) return;
    
    setDraft(prev => ({
      ...prev,
      blocks: prev.blocks.filter(b => b.id !== blockId)
    }));
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
    setHasUnsavedChanges(true);
  };

  // Add block
  const addBlock = (blockType) => {
    const newBlock = {
      id: `${blockType.toLowerCase()}_${Date.now().toString(36)}`,
      type: blockType,
      enabled: true,
      settings: {}
    };
    
    setDraft(prev => ({
      ...prev,
      blocks: [...prev.blocks, newBlock]
    }));
    setSelectedBlockId(newBlock.id);
    setShowAddBlockDialog(false);
    setHasUnsavedChanges(true);
  };

  // Save draft
  const saveDraft = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/page-layouts/home/draft`,
        { blocks: draft.blocks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Draft saved successfully');
      setHasUnsavedChanges(false);
      await fetchLayout();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  // Publish
  const publish = async () => {
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save draft first if there are unsaved changes
      if (hasUnsavedChanges) {
        await axios.put(
          `${API}/admin/page-layouts/home/draft`,
          { blocks: draft.blocks },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Then publish
      await axios.post(
        `${API}/admin/page-layouts/home/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Layout published! Changes are now live.');
      setHasUnsavedChanges(false);
      setShowPublishConfirm(false);
      await fetchLayout();
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Rollback
  const rollback = async () => {
    if (!rollbackVersion) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/page-layouts/home/rollback?version=${rollbackVersion}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Rolled back to version ${rollbackVersion}. Publish to make it live.`);
      setShowHistoryDialog(false);
      setRollbackVersion(null);
      await fetchLayout();
    } catch (error) {
      console.error('Error rolling back:', error);
      toast.error('Failed to rollback');
    }
  };

  // Reset to default
  const resetToDefault = async () => {
    if (!window.confirm('Reset to default layout? This will replace your current draft.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/page-layouts/home/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Reset to default layout');
      await fetchLayout();
    } catch (error) {
      console.error('Error resetting:', error);
      toast.error('Failed to reset');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="page-builder">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Layout className="w-5 h-5 text-orange-400" />
              Page Builder
            </h1>
            <Badge variant="outline" className="border-zinc-600">
              Homepage
            </Badge>
            {hasUnsavedChanges && (
              <Badge className="bg-yellow-500/20 text-yellow-400">
                Unsaved Changes
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Preview Mode Selector */}
            <div className="flex items-center bg-zinc-800 rounded-lg p-1 gap-1">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`p-1.5 rounded ${previewMode === 'desktop' ? 'bg-zinc-700' : ''}`}
                title="Desktop preview"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewMode('tablet')}
                className={`p-1.5 rounded ${previewMode === 'tablet' ? 'bg-zinc-700' : ''}`}
                title="Tablet preview"
              >
                <Tablet className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`p-1.5 rounded ${previewMode === 'mobile' ? 'bg-zinc-700' : ''}`}
                title="Mobile preview"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            
            {/* History Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistoryDialog(true)}
              className="border-zinc-700"
              disabled={history.length === 0}
            >
              <History className="w-4 h-4 mr-1" />
              History
            </Button>
            
            {/* Reset Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={resetToDefault}
              className="border-zinc-700"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            
            {/* Save Draft */}
            <Button
              variant="outline"
              size="sm"
              onClick={saveDraft}
              disabled={saving || !hasUnsavedChanges}
              className="border-zinc-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save Draft
            </Button>
            
            {/* Publish */}
            <Button
              size="sm"
              onClick={() => setShowPublishConfirm(true)}
              disabled={publishing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="publish-btn"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              Publish
            </Button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex max-w-7xl mx-auto">
        {/* Left: Block List */}
        <div className="w-80 flex-shrink-0 border-r border-zinc-800 p-4 h-[calc(100vh-57px)] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-zinc-300">Layout Blocks</h2>
            <Button
              size="sm"
              onClick={() => setShowAddBlockDialog(true)}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          
          <div className="space-y-2">
            {blocks.map((block, index) => (
              <DraggableBlock
                key={block.id}
                block={block}
                index={index}
                isSelected={selectedBlockId === block.id}
                onSelect={setSelectedBlockId}
                onToggle={(enabled) => toggleBlock(block.id, enabled)}
                onMoveUp={() => moveBlockUp(block.id)}
                onMoveDown={() => moveBlockDown(block.id)}
                onDelete={() => deleteBlock(block.id)}
                canMoveUp={index > 0}
                canMoveDown={index < blocks.length - 1}
                approvedBlocks={approvedBlocks}
              />
            ))}
          </div>
          
          {blocks.length === 0 && (
            <div className="text-center py-8 text-zinc-500">
              <Layout className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No blocks added yet</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => setShowAddBlockDialog(true)}
              >
                Add Block
              </Button>
            </div>
          )}
        </div>
        
        {/* Center: Preview Canvas */}
        <div className="flex-1 p-4 h-[calc(100vh-57px)] overflow-y-auto bg-zinc-950">
          <div 
            className={`
              mx-auto bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden transition-all
              ${previewMode === 'desktop' ? 'w-full' : ''}
              ${previewMode === 'tablet' ? 'max-w-2xl' : ''}
              ${previewMode === 'mobile' ? 'max-w-sm' : ''}
            `}
          >
            <div className="p-2 bg-zinc-800 text-xs text-zinc-400 flex items-center justify-between">
              <span>Preview - {previewMode}</span>
              <a 
                href="/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-400 hover:underline"
              >
                View Live Site →
              </a>
            </div>
            
            <div className="p-4 min-h-[400px]">
              {blocks.filter(b => b.enabled).map((block, index) => {
                const Icon = BLOCK_ICONS[block.type] || Layout;
                const colorClass = BLOCK_COLORS[block.type] || 'from-zinc-500/10 to-zinc-600/10 border-zinc-600/30';
                const blockInfo = approvedBlocks[block.type] || {};
                const settings = { ...(blockInfo.default_settings || {}), ...block.settings };
                
                return (
                  <div
                    key={block.id}
                    className={`
                      mb-3 p-4 rounded-lg border bg-gradient-to-r cursor-pointer transition-all
                      ${colorClass}
                      ${selectedBlockId === block.id ? 'ring-2 ring-orange-500' : ''}
                    `}
                    onClick={() => setSelectedBlockId(block.id)}
                    style={{
                      transform: `scale(${settings.card_scale || 1})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 opacity-70" />
                      <span className="text-sm font-medium opacity-80">
                        {settings.title || blockInfo.name || block.type}
                      </span>
                    </div>
                    
                    {/* Preview placeholder based on block type */}
                    <div 
                      className="bg-black/20 rounded flex items-center justify-center text-zinc-500 text-xs"
                      style={{ height: `${(settings.max_image_height || 200) * 0.5}px` }}
                    >
                      {block.type === 'HeroCarouselBlock' && 'Hero Carousel Preview'}
                      {block.type === 'FeaturedSeriesBlock' && `${settings.max_items || 12} Featured Series`}
                      {block.type === 'TrendingBlock' && `${settings.max_items || 10} Trending Items`}
                      {block.type === 'NewReleasesBlock' && `${settings.max_items || 12} New Releases`}
                      {block.type === 'GenreSpotlightBlock' && 'Genre Browser'}
                      {block.type === 'FeaturedCreatorsBlock' && `${settings.max_items || 12} Creators`}
                      {block.type === 'FeaturedArtworksBlock' && `${settings.max_items || 12} Artworks`}
                      {block.type === 'CTABannerBlock' && (settings.button_text || 'Call to Action')}
                      {block.type === 'NewsletterSignupBlock' && 'Newsletter Form'}
                    </div>
                  </div>
                );
              })}
              
              {blocks.filter(b => b.enabled).length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No enabled blocks to preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Right: Settings Panel */}
        <div className="w-80 flex-shrink-0 border-l border-zinc-800 p-4 h-[calc(100vh-57px)] overflow-y-auto">
          <h2 className="font-semibold text-zinc-300 mb-4 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Block Settings
          </h2>
          
          <BlockSettingsPanel
            block={selectedBlock}
            approvedBlocks={approvedBlocks}
            settingLimits={settingLimits}
            onUpdateSettings={updateBlockSettings}
          />
        </div>
      </div>
      
      {/* Add Block Dialog */}
      <Dialog open={showAddBlockDialog} onOpenChange={setShowAddBlockDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Block</DialogTitle>
            <DialogDescription>
              Choose a block type to add to your layout
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-2 py-4">
            {Object.entries(approvedBlocks).map(([type, info]) => {
              const Icon = BLOCK_ICONS[type] || Layout;
              const colorClass = BLOCK_COLORS[type] || 'from-zinc-500/20 to-zinc-600/20';
              
              return (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  className={`
                    p-3 rounded-lg border border-zinc-700 text-left
                    bg-gradient-to-r ${colorClass}
                    hover:border-orange-500/50 transition-colors
                  `}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{info.name}</span>
                  </div>
                  <p className="text-xs text-zinc-400">{info.description}</p>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              Rollback to a previous version
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4 max-h-80 overflow-y-auto">
            {history.map((item) => (
              <div
                key={item.version}
                className="flex items-center justify-between p-3 rounded-lg bg-zinc-800 border border-zinc-700"
              >
                <div>
                  <p className="font-medium">Version {item.version}</p>
                  <p className="text-xs text-zinc-500">
                    {item.published_at ? new Date(item.published_at).toLocaleString() : 'Unknown date'}
                  </p>
                  <p className="text-xs text-zinc-500">{item.blocks?.length || 0} blocks</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRollbackVersion(item.version)}
                  className="border-zinc-600"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Restore
                </Button>
              </div>
            ))}
            
            {history.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No version history available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-500" />
              Publish Layout
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will make your current layout live on the homepage immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300">
              Are you sure you want to publish this layout? The changes will be visible to all users right away.
            </p>
            {hasUnsavedChanges && (
              <p className="mt-2 text-yellow-400 text-sm flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                You have unsaved changes that will be saved before publishing.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPublishConfirm(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={publish}
              disabled={publishing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-publish-btn"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-1" />
              )}
              Yes, Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={!!rollbackVersion} onOpenChange={(open) => !open && setRollbackVersion(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-500" />
              Rollback to Version {rollbackVersion}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will replace your current draft with the selected version.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300">
              Are you sure you want to rollback to version {rollbackVersion}? Your current draft will be replaced. You'll need to publish to make the changes live.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRollbackVersion(null)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={rollback}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Yes, Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBuilder;
